import { useEffect, useRef, useState } from "react";
import Lenis from "lenis";
import { Toaster } from "sonner";
import "@/App.css";
import { Nav } from "./components/Nav";
import { Hero } from "./components/Hero";
import { AgeIndex } from "./components/AgeIndex";
import { Response } from "./components/Response";
import { HowItWorks } from "./components/HowItWorks";
import { WhySimple } from "./components/WhySimple";
import { ValueMoment } from "./components/ValueMoment";
import { EmotionalTurn } from "./components/EmotionalTurn";
import { MeetAndrea } from "./components/MeetAndrea";
import { Process } from "./components/Process";
import { Goals } from "./components/Goals";
import { JayeProof } from "./components/JayeProof";
import { Assessment } from "./components/Assessment";
import { Close } from "./components/Close";
import { Footer } from "./components/Footer";
import { TextAndrea } from "./components/TextAndrea";
import { Conversation } from "./components/Conversation";

import { AssessmentBooked } from "./components/AssessmentBooked";
import { PaymentComplete } from "./components/PaymentComplete";
import { PayRedirect } from "./components/PayRedirect";
import { ClientHub } from "./components/ClientHub";
import { PostPayment } from "./components/PostPayment";
import { ScheduleReview } from "./components/ScheduleReview";
import { ReviewConfirmed } from "./components/ReviewConfirmed";
import { TrustStrip } from "./components/TrustStrip";
import { ProblemAuthority } from "./components/ProblemAuthority";
import { InYourCorner } from "./components/InYourCorner";
import { NationalVirtual } from "./components/NationalVirtual";
import { DenverLocal } from "./components/DenverLocal";
import { GuidesIndex } from "./components/GuidesIndex";
import { ARTICLES } from "./pages/articles";
import { ContactFallback } from "./components/ContactFallback";
import { SeoPage } from "./components/SeoPage";
import { Resources } from "./components/Resources";
import { scrollToId, trackEvent } from "./lib/tracking";

const BOOKED_PATH = "/assessment-booked";
const PAYMENT_PATH = "/payment-complete";
const PAY_PREFIX = "/pay/";

// Google Ads sitelink destinations. Stage hashes auto-open that stage's sleep
// guide and land on it (the original /#newborn shareable link keeps working);
// HASH_ALIASES maps a public hash onto an existing section id so the stable
// ad URLs keep working even where the internal section id differs.
const STAGE_HASHES = { "newborn": "0-3", "newborn-sleep": "0-3", "infant-sleep": "4-11", "toddler-sleep": "2-4" };
const STAGE_EVENTS = { "0-3": "newborn_guide_visit", "4-11": "infant_guide_visit", "2-4": "toddler_guide_visit" };
const HASH_ALIASES = { "free-sleep-assessment": "free-assessment" };

// Standalone SEO pages (full site chrome, own metadata, indexable).
// These are the Google Ads landing destinations — real pages, not anchors.
const SEO_ROUTES = {
  "/pediatric-sleep-consultant": "pediatric-sleep-consultant",
  "/newborn-sleep-consultant": "newborn-sleep-consultant",
  "/baby-sleep-consultant": "baby-sleep-consultant",
  "/toddler-sleep-consultant": "toddler-sleep-consultant",
  "/virtual-sleep-consultant": "virtual-sleep-consultant",
  "/colorado-pediatric-sleep-consultant": "colorado-pediatric-sleep-consultant",
  "/denver-sleep-consultant": "denver-sleep-consultant",
  "/denver-in-home-sleep-consultant": "denver-in-home-sleep-consultant",
};

function App() {
  const [flowOpen, setFlowOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [prefillAge, setPrefillAge] = useState(null);
  const [visitorAge, setVisitorAge] = useState(null);

  const openFlow = (ageId = null) => {
    setPrefillAge(ageId);
    setFlowOpen(true);
  };

  // Intentional funnel entries carry the visitor's chosen stage, if any.
  const startFlow = () => openFlow(visitorAge);

  const path = window.location.pathname.replace(/\/$/, "");
  const isBookedPage = path === BOOKED_PATH;
  const isPaymentPage = path === PAYMENT_PATH;
  // /pay/<slug> — private enrollment links Andrea sends after the assessment.
  // Stage links (virtual-infant etc.) open a short private enrollment page
  // whose CTA stamps the visitor's GA4 identity onto the Stripe Payment Link;
  // legacy base links keep the instant redirect.
  const isPayPage = path.startsWith(PAY_PREFIX);
  const paySlug = isPayPage ? path.slice(PAY_PREFIX.length).split("/")[0] : null;
  // /client-hub — internal staff tool (server-authenticated; login screen
  // only until the session checks out).
  const isHubPage = path === "/client-hub";
  // Six private post-payment welcome pages + the plan-review scheduling page.
  const WELCOME_SLUGS = ["virtual-newborn", "virtual-infant", "virtual-toddler", "in-home-newborn", "in-home-infant", "in-home-toddler"];
  const welcomeSlug = WELCOME_SLUGS.includes(path.slice(1)) ? path.slice(1) : null;
  const isSchedulePage = path === "/schedule-sleep-plan-review";
  // /sleep-plan-review-confirmed — Calendly's after-booking redirect target.
  const isReviewConfirmedPage = path === "/sleep-plan-review-confirmed";
  const seoSlug = SEO_ROUTES[path] || null;
  // Sleep guides: /sleep-guides index + /sleep-guides/<slug> articles,
  // rendered through the same SEO-page chrome. Unknown guide slugs soften
  // to the index rather than dead-ending.
  const isGuidesIndex = path === "/sleep-guides" || path === "/sleep-guides/";
  const guideSlug = !isGuidesIndex && path.startsWith("/sleep-guides/")
    ? path.slice("/sleep-guides/".length).split("/")[0]
    : null;
  const guidePage = guideSlug ? ARTICLES[guideSlug] : null;
  const isStandalonePage = isBookedPage || isPaymentPage || isPayPage || isHubPage || !!welcomeSlug || isSchedulePage || isReviewConfirmedPage;

  useEffect(() => {
    if (isStandalonePage) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ lerp: 0.1, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  // Deep links (/#meet-andrea etc.): the browser's native fragment jump runs
  // before React paints, so honor the hash ourselves once sections exist.
  // Stage hashes auto-open that stage's sleep guide and land on it, no clicks
  // required — these are cold direct landings from Google Ads sitelinks.
  const stageVisitFired = useRef(false);
  useEffect(() => {
    if (isStandalonePage) return;
    let cleanupLanding = () => {};
    const handleHash = () => {
      cleanupLanding();
      cleanupLanding = () => {};
      const id = window.location.hash.replace("#", "");
      if (!id) return;
      const stage = STAGE_HASHES[id];
      const target = stage ? "your-stage" : HASH_ALIASES[id] || id;
      if (stage) {
        setVisitorAge(stage);
        if (!stageVisitFired.current) {
          stageVisitFired.current = true;
          // Secondary engagement measurement only — never a conversion.
          // trackEvent pushes the dataLayer hook AND forwards to GA4 via gtag.
          trackEvent(STAGE_EVENTS[stage], { source: "direct_link" });
        }
      }
      // Page imagery settles asynchronously — scroll after React commits, then
      // re-target once layout has settled (and again on full load for slow
      // networks). If the visitor starts scrolling manually, we stand down.
      let visitorSteering = false;
      const onSteer = () => { visitorSteering = true; };
      window.addEventListener("wheel", onSteer, { passive: true });
      window.addEventListener("touchmove", onSteer, { passive: true });
      const land = () => { if (!visitorSteering) scrollToId(target); };
      const raf = requestAnimationFrame(() => requestAnimationFrame(land));
      const t1 = setTimeout(land, 1400);
      const onLoad = () => setTimeout(land, 150);
      if (document.readyState !== "complete") {
        window.addEventListener("load", onLoad, { once: true });
      }
      cleanupLanding = () => {
        cancelAnimationFrame(raf);
        clearTimeout(t1);
        window.removeEventListener("wheel", onSteer);
        window.removeEventListener("touchmove", onSteer);
        window.removeEventListener("load", onLoad);
      };
    };
    handleHash();
    window.addEventListener("hashchange", handleHash);
    return () => {
      cleanupLanding();
      window.removeEventListener("hashchange", handleHash);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isPayPage) {
    return (
      <div className="bg-ivory text-ink antialiased">
        <PayRedirect slug={paySlug || ""} />
        <ContactFallback />
        <div aria-hidden="true" className="grain-overlay" />
      </div>
    );
  }

  if (isHubPage) {
    return (
      <div className="bg-ivory text-ink antialiased">
        <ClientHub />
        <div aria-hidden="true" className="grain-overlay" />
      </div>
    );
  }

  if (isBookedPage) {
    return (
      <div className="bg-ivory text-ink antialiased">
        <AssessmentBooked />
        <ContactFallback />
        <div aria-hidden="true" className="grain-overlay" />
      </div>
    );
  }

  if (isPaymentPage) {
    return (
      <div className="bg-ivory text-ink antialiased">
        <PaymentComplete />
        <ContactFallback />
        <div aria-hidden="true" className="grain-overlay" />
      </div>
    );
  }

  if (welcomeSlug) {
    const welcomeService = welcomeSlug.startsWith("in-home") ? "in-home" : "virtual";
    const welcomeStage = welcomeSlug.split("-").pop();
    return (
      <div className="bg-ivory text-ink antialiased">
        <PostPayment service={welcomeService} stage={welcomeStage} />
        <ContactFallback />
        <div aria-hidden="true" className="grain-overlay" />
      </div>
    );
  }

  if (isSchedulePage) {
    return (
      <div className="bg-ivory text-ink antialiased">
        <ScheduleReview />
        <ContactFallback />
        <div aria-hidden="true" className="grain-overlay" />
      </div>
    );
  }

  if (isReviewConfirmedPage) {
    return (
      <div className="bg-ivory text-ink antialiased">
        <ReviewConfirmed />
        <ContactFallback />
        <div aria-hidden="true" className="grain-overlay" />
      </div>
    );
  }

  if (isGuidesIndex) {
    return (
      <div className="bg-ivory text-ink antialiased">
        <GuidesIndex />
        <div aria-hidden="true" className="grain-overlay" />
      </div>
    );
  }

  if (guideSlug) {
    return (
      <div className="bg-ivory text-ink antialiased">
        {guidePage ? <SeoPage slug={guideSlug} page={guidePage} /> : <GuidesIndex />}
        <div aria-hidden="true" className="grain-overlay" />
      </div>
    );
  }

  // Standalone SEO pages get the full site chrome (Nav, Footer, concierge)
  // and Lenis — only the homepage section-hash logic is skipped for them.
  if (seoSlug) {
    return (
      <div className="bg-ivory text-ink antialiased">
        <SeoPage slug={seoSlug} />
        <div aria-hidden="true" className="grain-overlay" />
      </div>
    );
  }

  return (
    <div className="bg-ivory text-ink antialiased">
      <Nav onStart={startFlow} open={menuOpen} setOpen={setMenuOpen} />
      <main>
        <Hero />
        <TrustStrip />
        <HowItWorks />
        <AgeIndex selected={visitorAge} onSelect={setVisitorAge} />
        <Response visitorAge={visitorAge} onSelect={setVisitorAge} onStart={openFlow} />
        <ProblemAuthority />
        <WhySimple />
        <ValueMoment />
        <EmotionalTurn />
        <InYourCorner />
        <MeetAndrea />
        <Process />
        <Goals />
        <NationalVirtual />
        <DenverLocal />
        <Resources />
        <JayeProof />
        <Assessment onStart={startFlow} />
        <Close onStart={startFlow} />
      </main>
      <Footer />
      <TextAndrea hidden={flowOpen || menuOpen} />
      <Conversation
        open={flowOpen}
        prefillAge={prefillAge}
        onAgeSelected={setVisitorAge}
        onClose={() => setFlowOpen(false)}
      />
      <ContactFallback />
      <Toaster position="top-center" toastOptions={{ style: { background: "#241C13", color: "#FAF6EF", border: "none" } }} />
      <div aria-hidden="true" className="grain-overlay" />
    </div>
  );
}

export default App;
